import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Paper } from '@mui/material';
import { Email, Lock, ArrowBack } from '@mui/icons-material';
import { generateOtp, isOtpValid, sendOtpEmail } from './OtpService';
import { auth, firestore } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import Cookies from 'js-cookie';
import { generateJWT } from '../APIs/generateJWT';
import { toast } from 'react-toastify';

const OtpLogin = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const navigate = useNavigate();

  const OTP_EXPIRY_MINUTES = 5;

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
  };

  const storeUserProfile = async (userEmail) => {
    const q = query(
      collection(firestore, "UserProfileMaster"),
      where("email", "==", userEmail)
    );
    const snap = await getDocs(q);
    return !snap.empty;
  };

  const getUserRoleAndTenant = async (userEmail) => {
    const q = query(
      collection(firestore, "UserProfileMaster"),
      where("email", "==", userEmail)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docData = snap.docs[0].data();
      let role = (docData.role || "user").toLowerCase().trim();
      if (["superadmin", "super-admin", "super_admin"].includes(role)) role = "superadmin";
      else if (["admin", "administrator"].includes(role)) role = "admin";
      else if (["user", "enduser", "end-user"].includes(role)) role = "user";
      const tenantID = docData.tenantID || "";
      const displayName = docData.displayName || docData.name || userEmail.split('@')[0];
      return { role, tenantID, displayName };
    }
    return { role: null, tenantID: null, displayName: null };
  };

  const handleSendOtp = async () => {
    if (!email) {
      showMessage("Please enter your email address.", "error");
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      showMessage("Please enter a valid email address.", "error");
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Check if user is registered in the system
      const isRegistered = await storeUserProfile(email);
      if (!isRegistered) {
        showMessage("You are not registered in the system. Please contact your administrator for access.", "error");
        setIsLoading(false);
        return;
      }

      const newOtp = generateOtp();
      setGeneratedOtp(newOtp);
      setGeneratedAt(Date.now());

      const result = await sendOtpEmail(email, newOtp);
      
      if (result.success) {
        setOtpSent(true);
        showMessage(`OTP sent to ${email}. Please check your inbox (including spam folder).`, "success");
      } else {
        showMessage("Failed to send OTP. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      showMessage("Error sending OTP. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      showMessage("Please enter the OTP.", "error");
      return;
    }

    if (otp.length !== 6) {
      showMessage("OTP must be 6 digits.", "error");
      return;
    }

    if (!isOtpValid(generatedAt, OTP_EXPIRY_MINUTES)) {
      showMessage("OTP has expired. Please request a new one.", "error");
      return;
    }

    if (otp !== generatedOtp) {
      showMessage("Invalid OTP. Please try again.", "error");
      return;
    }

    setIsLoading(true);

    try {
      // Get user role, tenant, and displayName
      const { role, tenantID, displayName } = await getUserRoleAndTenant(email);
      const isSuperAdmin = role === "superadmin";
      const isAdmin = role === "admin" || isSuperAdmin;
      
      // Store session data
      sessionStorage.setItem("userRole", role);
      sessionStorage.setItem("isSuperAdmin", isSuperAdmin ? "true" : "false");
      sessionStorage.setItem("isAdmin", isAdmin ? "true" : "false");
      sessionStorage.setItem("userName", displayName);
      
      if (!isSuperAdmin) {
        sessionStorage.setItem("tenantID", tenantID);
      } else {
        sessionStorage.removeItem("tenantID");
      }

      // Generate JWT token
      let token = "dummy-token";
      if (process.env.REACT_APP_ENV === "production") {
        const jwt = await generateJWT(email, tenantID, role);
        token = jwt.token;
      }

      // Set tokens
      Cookies.set("jwtToken", token, { path: "/" });
      sessionStorage.setItem("jwtToken", token);

      showMessage("Login successful! Redirecting...", "success");
      toast.success("Login successful!");

      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);

    } catch (error) {
      console.error("Error during login:", error);
      showMessage("Login failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setEmail('');
    setOtp('');
    setGeneratedOtp('');
    setGeneratedAt(null);
    setOtpSent(false);
    setMessage('');
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {onBack && (
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
        )}

        <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
          Email OTP Login
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email address to receive a one-time password for secure login.
        </Typography>

        {message && (
          <Alert severity={messageType} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        {!otpSent ? (
          <Box>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSendOtp}
              disabled={isLoading || !email}
              startIcon={isLoading ? <CircularProgress size={20} /> : <Lock />}
              sx={{ py: 1.5 }}
            >
              {isLoading ? 'Sending...' : 'Send OTP'}
            </Button>
          </Box>
        ) : (
          <Box>
            <TextField
              fullWidth
              label="Enter OTP"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={isLoading}
              inputProps={{ maxLength: 6 }}
              InputProps={{
                startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 3 }}
              placeholder="Enter 6-digit code"
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.length !== 6}
              startIcon={isLoading ? <CircularProgress size={20} /> : <Lock />}
              sx={{ py: 1.5, mb: 2 }}
            >
              {isLoading ? 'Verifying...' : 'Verify & Login'}
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isLoading}
                size="small"
              >
                Use Different Email
              </Button>
              <Button
                variant="text"
                onClick={handleSendOtp}
                disabled={isLoading}
                size="small"
              >
                Resend OTP
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              OTP expires in {OTP_EXPIRY_MINUTES} minutes
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OtpLogin;
